import { Autocomplete, FormControl, FormLabel, TextField } from '@mui/material';
import { useEffect } from 'react';

import { useDAL } from '@/contexts/useDAL';

import { EmptySelect } from './emptySelect';

import { TextDetailsTuple } from '../text/textDetailsTuple';

const title = 'Subnet';

export const AWSSubnetSelect = ({
  accountId,
  regionName,
  subnetId,
  onChange,
  hideLabel,
  availabilityZoneScope,
  vpcScope,
  excludeSubnetIds,
}: {
  accountId?: string;
  regionName?: string;
  subnetId?: string;
  subnetName?: string;
  availabilityZoneScope?: string;
  vpcScope: string;
  onChange: (subnetId?: string) => void;
  hideLabel?: boolean;
  excludeSubnetIds?: string[];
}) => {
  if (!accountId || !regionName || !vpcScope) {
    return <EmptySelect title={hideLabel ? '' : title} />;
  }

  return (
    <InnerAWSSubnetSelect
      accountId={accountId}
      regionName={regionName}
      subnetId={subnetId}
      onChange={onChange}
      hideLabel={hideLabel}
      availabilityZoneScope={availabilityZoneScope}
      vpcScope={vpcScope}
      excludeSubnetIds={excludeSubnetIds}
    />
  );
};

const InnerAWSSubnetSelect = ({
  accountId,
  regionName,
  subnetId,
  onChange,
  hideLabel,
  availabilityZoneScope,
  vpcScope,
  excludeSubnetIds,
}: {
  accountId: string;
  regionName: string;
  subnetId?: string;
  availabilityZoneScope?: string;
  vpcScope?: string;
  onChange: (subnetId?: string) => void;
  hideLabel?: boolean;
  excludeSubnetIds?: string[];
}) => {
  const dal = useDAL();
  const { data: body, isLoading } = dal.restore.awsSubnets.useList(
    accountId,
    regionName
  );

  useEffect(() => {
    if (isLoading) {
      return;
    }

    if (subnetId && !body?.subnets?.some((x) => x.id === subnetId)) {
      onChange(undefined);
    }
  }, [body?.subnets, isLoading, onChange, subnetId]);

  if (!body?.subnets?.length) {
    return <EmptySelect title={hideLabel ? '' : title} />;
  }

  const availableSubnets = body.subnets
    .filter(
      (x) =>
        !availabilityZoneScope || x.availabilityZone === availabilityZoneScope
    )
    .filter((x) => !vpcScope || x.vpc === vpcScope)
    .filter((x) => !excludeSubnetIds?.includes(x.id));

  const selectedSubnet = availableSubnets.find((x) => x.id === subnetId);

  return (
    <FormControl size='small' className='flex-1'>
      {!hideLabel && <FormLabel>{title}</FormLabel>}
      <Autocomplete
        clearOnBlur
        data-testid='awsSubnetSelect'
        size='small'
        value={selectedSubnet || null}
        onChange={(event, newValue) => onChange(newValue?.id || undefined)}
        getOptionLabel={(option) => option?.id || ''}
        filterOptions={(options, state) => {
          const input = state.inputValue.trim().toLowerCase();
          if (!input) return options;
          return options.filter((o) => {
            const id = o.id?.toLowerCase() ?? '';
            const name = o.name?.toLowerCase() ?? '';
            return id.includes(input) || name.includes(input);
          });
        }}
        renderOption={(props, option) => (
          <li {...props} key={option.id}>
            <TextDetailsTuple
              primaryProp={option.id}
              secondaryProp={option.name || option.id}
              enableSameValue={true}
            />
          </li>
        )}
        isOptionEqualToValue={(option, value) => option?.id === value?.id}
        renderInput={(params) => {
          return (
            <TextField
              {...params}
              placeholder='Select'
              label=''
              InputLabelProps={{ shrink: true }}
            />
          );
        }}
        options={availableSubnets}
      />
    </FormControl>
  );
};
